import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquarePlus, Trash2, Clock, TrendingUp, Filter, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CoachNotesSummary from "@/components/CoachNotesSummary";
import VoiceNoteRecorder from "@/components/VoiceNoteRecorder";
import SendRecapModal from "@/components/SendRecapModal";

/**
 * Append-only timeline of coach observations for a single player.
 *
 * Written by admins OR any coach who has booked with the player (server-side
 * gate). Each entry captures a snapshot of the player's DUPR-style level at
 * the time of writing so a coach flicking through history can see whether
 * "poor backhand consistency" was recorded when the player was 3.0 vs 3.5.
 *
 * `context` (general / assessment / session) is filterable in the UI —
 * particularly useful during the rating step of a fresh assessment when a
 * coach wants to remind themselves of every previous assessment note.
 *
 * The parent decides where this lives (PlayerDetail sidebar, admin user
 * page, assessment panel). Pass `defaultContext="assessment"` from the
 * assessment page so new entries auto-tag themselves.
 */
export default function CoachNotesTimeline({ studentId, defaultContext = "general", showTitle = true, className = "" }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [context, setContext] = useState(defaultContext);
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [recapFor, setRecapFor] = useState(null); // {noteId, body, studentName}

  const canWrite = user?.role === "coach" || user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/coach-notes`);
      setNotes(data || []);
    } catch (e) {
      // Silent — the endpoint 403s for random coaches; that's expected.
      setNotes([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (studentId) load();   }, [studentId]);

  // Cross-component refresh — the FAB voice recorder + other places dispatch
  // `coach-notes:changed` when they add/remove a note. Listen and reload so
  // this list stays in sync without prop drilling.
  useEffect(() => {
    const handler = (e) => {
      if (!e?.detail?.studentId || e.detail.studentId === studentId) load();
    };
    window.addEventListener("coach-notes:changed", handler);
    return () => window.removeEventListener("coach-notes:changed", handler);
     
  }, [studentId]);

  const filtered = useMemo(() => {
    if (filter === "all") return notes;
    return notes.filter((n) => (n.context || "general") === filter);
  }, [notes, filter]);

  const grouped = useMemo(() => {
    // Group by skill_level_at_time (as strings like "3.5") so the coach can
    // see all notes recorded while the player was at each level.
    const g = new Map();
    for (const n of filtered) {
      const key = n.skill_level_at_time !== null && n.skill_level_at_time !== undefined ? `Level ${Number(n.skill_level_at_time).toFixed(1)}` : "Level —";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(n);
    }
    // Sort by level DESC (highest first) so most recent progression is up top,
    // notes within each level are already newest-first from the API.
    return [...g.entries()].sort((a, b) => {
      const na = parseFloat(a[0].replace("Level ", "")) || 0;
      const nb = parseFloat(b[0].replace("Level ", "")) || 0;
      return nb - na;
    });
  }, [filtered]);

  const add = async () => {
    const trimmed = body.trim();
    if (trimmed.length < 2) { toast.error("Add at least 2 characters"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/students/${studentId}/coach-notes`, {
        body: trimmed, context,
      });
      setNotes((ns) => [data, ...ns]);
      setBody("");
      toast.success("Note added");
      window.dispatchEvent(new CustomEvent("coach-notes:changed", { detail: { studentId } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Remove this note? It's gone for everyone.")) return;
    try {
      await api.delete(`/students/${studentId}/coach-notes/${noteId}`);
      setNotes((ns) => ns.filter((n) => n.id !== noteId));
      toast.success("Note removed");
      window.dispatchEvent(new CustomEvent("coach-notes:changed", { detail: { studentId } }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
    }
  };

  return (
    <section className={`card-surface p-5 ${className}`} data-testid="coach-notes-timeline">
      {showTitle && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="label-mono mb-1 flex items-center gap-1.5" style={{ color: "#CCFF00" }}>
              <TrendingUp className="w-3.5 h-3.5" /> COACH JOURNAL · {notes.length} entries
            </div>
            <p className="text-xs text-[#A3A3A3] leading-relaxed max-w-xl">
              Append-only observations. Every note is stamped with the player&apos;s level at the time so you can compare
              legacy notes with what you&apos;re seeing today.
            </p>
          </div>
          {notes.length > 0 && (
            <div className="inline-flex items-center gap-1 border border-[#262626] bg-[#0A0A0A] p-1" data-testid="coach-notes-filter">
              <Filter className="w-3 h-3 text-[#525252] ml-2" />
              {[
                { v: "all", l: "All" },
                { v: "assessment", l: "Assessment" },
                { v: "session", l: "Session" },
                { v: "general", l: "General" },
              ].map((f) => {
                const on = filter === f.v;
                return (
                  <button
                    key={f.v}
                    type="button"
                    onClick={() => setFilter(f.v)}
                    className="px-2 py-1 text-[10px] uppercase tracking-wider transition-colors"
                    style={on ? { background: "#CCFF00", color: "#0A0A0A", fontWeight: 700 } : { color: "#A3A3A3" }}
                    data-testid={`coach-notes-filter-${f.v}`}
                  >
                    {f.l}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* AI Summary */}
      {canWrite && notes.length > 0 && (
        <div className="mb-4" data-testid="coach-notes-summary-slot">
          <CoachNotesSummary studentId={studentId} currentNotesCount={notes.length} />
        </div>
      )}

      {/* Add note */}
      {canWrite && (
        <div className="mb-4 border border-[#262626] p-3" data-testid="coach-notes-add">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <MessageSquarePlus className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono">ADD NEW NOTE</div>
            <select
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="ml-auto bg-[#0A0A0A] border border-[#262626] text-[10px] uppercase tracking-wider text-[#A3A3A3] px-2 py-1 outline-none focus:border-[#CCFF00]"
              data-testid="coach-notes-context"
            >
              <option value="general">General</option>
              <option value="assessment">Assessment</option>
              <option value="session">Session</option>
            </select>
          </div>
          <div className="mb-2">
            <VoiceNoteRecorder
              studentId={studentId}
              studentName={notes[0]?.student_name || ""}
              variant="inline"
              defaultContext={context === "general" ? "session" : context}
              onSaved={load}
            />
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Backhand cross-court sharper today. Still struggling with third-shot drop under pressure — try slower drills next session."
            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none leading-relaxed"
            data-testid="coach-notes-body"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] text-[#525252] uppercase tracking-wider">{body.length} / 2000</div>
            <button
              type="button"
              onClick={add}
              disabled={saving || body.trim().length < 2}
              className="btn-volt px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="coach-notes-save"
            >
              {saving ? "Saving…" : "Add note"}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="text-xs text-[#525252] uppercase tracking-wider">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[#525252] text-center py-6 border border-dashed border-[#262626]" data-testid="coach-notes-empty">
          No notes yet. Your first entry starts the timeline.
        </div>
      ) : (
        <div className="space-y-5 max-h-[560px] overflow-y-auto pr-1" data-testid="coach-notes-list">
          {grouped.map(([levelKey, rows]) => (
            <div key={levelKey}>
              <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#0f0f0f] py-1 z-10">
                <span className="label-mono text-[#CCFF00]" data-testid={`coach-notes-group-${levelKey.replace(/\s+/g,'-').toLowerCase()}`}>
                  {levelKey}
                </span>
                <span className="text-[10px] text-[#525252] uppercase tracking-wider">· {rows.length} note{rows.length === 1 ? "" : "s"}</span>
              </div>
              <div className="space-y-2">
                {rows.map((n) => {
                  const mine = n.author_id === user?.id;
                  const canRemove = mine || user?.role === "admin" || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
                  return (
                    <div key={n.id} className="border border-[#262626] p-3" data-testid={`coach-note-${n.id}`}>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1.5">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(n.created_at).toLocaleString()}</span>
                        <span className="text-[#525252]">·</span>
                        <span className="text-white">{n.author_name || "—"}</span>
                        <span
                          className="label-mono px-1.5 py-0.5"
                          style={{
                            background: n.context === "assessment" ? "#FF4500" : n.context === "session" ? "#CCFF00" : "#262626",
                            color: n.context === "assessment" ? "#FFFFFF" : n.context === "session" ? "#0A0A0A" : "#A3A3A3",
                          }}
                        >
                          {(n.context || "general").toUpperCase()}
                        </span>
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => setRecapFor({ noteId: n.id, body: n.body, studentName: n.student_name || "" })}
                            className="ml-auto text-[#A3A3A3] hover:text-[#CCFF00] p-1 transition-colors"
                            title="Send this as a recap to the player"
                            data-testid={`coach-note-recap-${n.id}`}
                          >
                            <Send className="w-3 h-3" />
                          </button>
                        )}
                        {canRemove && (
                          <button
                            type="button"
                            onClick={() => remove(n.id)}
                            className={`${canWrite ? "" : "ml-auto"} text-[#FF4500] hover:bg-[#FF4500]/10 p-1 transition-colors`}
                            title="Remove note"
                            data-testid={`coach-note-remove-${n.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-sm text-[#E5E5E5] whitespace-pre-wrap leading-relaxed" data-testid={`coach-note-body-${n.id}`}>
                        {n.body}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <SendRecapModal
        open={!!recapFor}
        onClose={() => setRecapFor(null)}
        studentId={studentId}
        studentName={recapFor?.studentName || ""}
        sourceBody={recapFor?.body || ""}
        noteId={recapFor?.noteId || ""}
      />
    </section>
  );
}
